import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { Injectable } from "@angular/core";
import { ApiResponse } from "../_model/api-response";
import { CreateUser, UserDto } from "../_model/users";
import { WalletDetailsDto, WalletTransactionDto } from "../_model/wallet";
import { CadDepositInstructionsResponseDto, ConfirmDepositDto, CreateDepositDto, DepositDto, DepositFormConfigResponseDto, InteracEmailResponseDto, UpsertInteracEmailRequestDto, VirtualAccountResponseDto } from "../_model/deposits";
import { CreateOrder, OrderDetailsDto, OrderDto, OrderLineageDto, OrderSettlementDto } from "../_model/order";
import { WithdrawalDto } from "../_model/withdrawal";
import { MainDataDto, MainSnapshotDto } from "../_model/main";
import { AddCurrencyCountry, CountryCurrencyDto, CountryDto, CreateCountry } from "../_model/countries";
import { CreateCurrencyDto, CurrencyDetailsDto } from "../_model/currency";
import { CandleResponseDto, MarketDepthDto } from "../_model/market";
import { DepositStatusResponse, VerifyFlutterwaveRedirectRequest } from "../_model/payments";
import {
    PaymentGatewayProviderDto,
    CreatePaymentGatewayProviderRequest,
    PaymentGatewayConfigDto,
    CreatePaymentGatewayConfigRequest,
    CurrencyPaymentGatewayDto,
    CreateCurrencyPaymentGatewayRequest
} from "../_model/payment-admin";
import {
    AdminCountryChangeRequestDto,
    AdminCountryChangeRequestResponseDto,
    CancelCountryChangeRequestResponseDto,
    CountryChangeRequestDto,
    CountryChangeRequestFilters,
    CountryChangeRequestListItemDto,
    CreateCountryChangeRequestDto,
    ReviewCountryChangeRequestDto,
    UserCountryChangeRequestResponseDto
} from '../_model/country-change';
import {
    AdminFinancePnlReportDto,
    FinanceReferenceRateDto,
    FinanceReportFilters,
    TradeProfitSnapshotListItemDto,
    UpsertFinanceReferenceRateDto
} from "../_model/finance-report";

import {
    AdminComplianceFlagListItemDto,
    ResolveComplianceFlagRequestDto
} from '../_model/compliance';
import { PaymentWebhookLogDetailsDto, PaymentWebhookLogListItemDto } from "../_model/payment-webhook-log";
import { AdminMenuCountersDto } from "../_model/admin-menu-counters";
import {
    AccountDeletionEligibilityDto,
    AccountLifecycleActionResponseDto,
    DeactivateAccountRequestDto,
    DeleteAccountRequestDto
} from "../_model/account-lifecycle";

@Injectable({ providedIn: 'root' })
export class ApiService {

    private base = environment.apiBaseUrl;
    private pageSize = environment.pageSize;

    constructor(private http: HttpClient) { }

    getMain() {
        return this.http.get<ApiResponse<MainDataDto>>(`${this.base}/Main`);
    }

    getMarketDepth(base: string, quote: string, depth = 20, group = 0.25) {
        return this.http.get<ApiResponse<MarketDepthDto>>(
            `${this.base}/Market/depth`,
            { params: { base, quote, depth, group } as any }
        );
    }

    getCandles(base: string, quote: string, tf: string, limit = 120) {
        return this.http.get<ApiResponse<CandleResponseDto>>(
            `${environment.apiBaseUrl}/Market/candles`,
            { params: { base, quote, tf, limit } as any }
        );
    }

    getMainStats(from: string, to: string) {
        return this.http.get<ApiResponse<MainSnapshotDto>>(`${this.base}/Main/market-snapshot?from=${from}&to=${to}`);
    }

    getProfile() {
        return this.http.get<ApiResponse<UserDto>>(
            `${this.base}/users/profile`
        );
    }

    downloadAccountData() {
        return this.http.get(`${this.base}/Users/account-data`, {
            observe: 'response',
            responseType: 'blob'
        });
    }

    getAccountDeletionEligibility() {
        return this.http.get<ApiResponse<AccountDeletionEligibilityDto>>(
            `${this.base}/Users/account/deletion-eligibility`
        );
    }

    deactivateAccount(payload: DeactivateAccountRequestDto) {
        return this.http.post<ApiResponse<AccountLifecycleActionResponseDto>>(
            `${this.base}/Users/account/deactivate`,
            payload
        );
    }

    deleteAccount(payload: DeleteAccountRequestDto) {
        return this.http.post<ApiResponse<AccountLifecycleActionResponseDto>>(
            `${this.base}/Users/account/delete`,
            payload
        );
    }
    getUsers(page: number) {
        return this.http.get<ApiResponse<UserDto[]>>(
            `${this.base}/users?page=${page}&size=${this.pageSize}`
        );
    }
    getSingleWallet(walletId: string) {
        return this.http.get<ApiResponse<WalletDetailsDto>>(
            `${this.base}/Wallets/${walletId}`
        );
    }

    getAdminUserWallets(userId: string, page: number) {
        return this.http.get<ApiResponse<WalletDetailsDto[]>>(
            `${this.base}/Wallets/users/${userId}/?page=${page}&size=${this.pageSize}`
        );
    }

    addAdmin(payload: CreateUser) {
        return this.http.post<ApiResponse<UserDto>>(
            `${this.base}/users`,
            payload
        );
    }

    createCurrency(payload: CreateCurrencyDto) {
        return this.http.post<ApiResponse<CurrencyDetailsDto>>(
            `${this.base}/currency`,
            payload
        );
    }

    createCountry(payload: CreateCountry) {
        return this.http.post<ApiResponse<CountryDto>>(
            `${this.base}/country`,
            payload
        );
    }

    initializePayment(payload: CreateDepositDto) {
        return this.http.post<ApiResponse<DepositDto>>(
            `${this.base}/deposits/initiate`,
            payload
        );
    }

    confirmPayment(payload: ConfirmDepositDto) {
        return this.http.post<ApiResponse<DepositDto>>(
            `${this.base}/deposits/confirm`,
            payload
        );
    }

    getDepositChannels(walletId: string, amount = 0) {
        return this.http.get<ApiResponse<DepositFormConfigResponseDto>>(
            `${this.base}/deposits/wallets/${encodeURIComponent(walletId)}/channels`,
            { params: { amount: String(amount || 0) } }
        );
    }

    getDeposit(depositId: string) {
        return this.http.get<ApiResponse<DepositDto>>(
            `${this.base}/deposits/${depositId}`
        );
    }

    getUserDeposits(page: number) {
        return this.http.get<ApiResponse<DepositDto[]>>(
            `${this.base}/deposits/user?page=${page}&size=${this.pageSize}`
        );
    }

    getWalletDeposits(walletId: string, page: number) {
        return this.http.get<ApiResponse<DepositDto[]>>(
            `${this.base}/Wallets/${walletId}/deposits?page=${page}&size=${this.pageSize}`
        );
    }

    getWalletWithdrawals(walletId: string, page: number) {
        return this.http.get<ApiResponse<WithdrawalDto[]>>(
            `${this.base}/Wallets/${walletId}/withdrawals?page=${page}&size=${this.pageSize}`
        );
    }

    getWalletTransactions(walletId: string, page: number) {
        return this.http.get<ApiResponse<WalletTransactionDto[]>>(
            `${this.base}/Wallets/${walletId}/transactions?page=${page}&size=${this.pageSize}`
        );
    }

    getWalletSettlements(walletId: string, page: number) {
        return this.http.get<ApiResponse<OrderSettlementDto[]>>(
            `${this.base}/Wallets/${walletId}/settlements?page=${page}&size=${this.pageSize}`
        );
    }

    getWalletOrders(walletId: string, page: number) {
        return this.http.get<ApiResponse<OrderDetailsDto[]>>(
            `${this.base}/Wallets/${walletId}/orders?page=${page}&size=${this.pageSize}`
        );
    }

    createOrder(payload: CreateOrder) {
        return this.http.post<ApiResponse<OrderDto>>(
            `${this.base}/orders`,
            payload
        );
    }

    getOrder(orderId: string) {
        return this.http.get<ApiResponse<OrderDetailsDto>>(
            `${this.base}/orders/${orderId}`
        );
    }

    getUserOrders(page: number) {
        return this.http.get<ApiResponse<OrderDto[]>>(
            `${this.base}/orders/user?page=${page}&size=${this.pageSize}`
        );
    }

    getAdminOrders(page: number) {
        return this.http.get<ApiResponse<OrderDto[]>>(
            `${this.base}/orders?page=${page}&size=${this.pageSize}`
        );
    }

    getAdminUserOrders(userId: string, page: number) {
        return this.http.get<ApiResponse<OrderDto[]>>(
            `${this.base}/orders/users/${userId}/?page=${page}&size=${this.pageSize}`
        );
    }

    getUserOrdersByStatus(status: string, page: number) {
        return this.http.get<ApiResponse<OrderDto[]>>(
            `${this.base}/orders/user/status/${status}?page=${page}&size=${this.pageSize}`
        );
    }

    getAdminOrdersByStatus(status: string, page: number) {
        return this.http.get<ApiResponse<OrderDto[]>>(
            `${this.base}/orders/status/${status}?page=${page}&size=${this.pageSize}`
        );
    }

    searchUserOrdersByStatus(query: string, page: number) {
        return this.http.get<ApiResponse<OrderDto[]>>(
            `${this.base}/orders/user/search/${query}?page=${page}&size=${this.pageSize}`
        );
    }

    searchAdminOrders(query: string, page: number) {
        return this.http.get<ApiResponse<OrderDto[]>>(
            `${this.base}/orders/search/${query}?page=${page}&size=${this.pageSize}`
        );
    }

    createWallet(currency: string) {
        return this.http.post<ApiResponse<WalletDetailsDto>>(
            `${this.base}/wallets`,
            { currency }
        );
    }

    cancelOrder(orderId: string) {
        return this.http.post<ApiResponse<OrderDetailsDto>>(
            `${this.base}/orders/${orderId}/cancel`,
            {}
        );
    }

    updateOrderRate(orderId: string, newRate: number) {
        return this.http.patch<ApiResponse<OrderDetailsDto>>(
            `${this.base}/orders/${orderId}/rate`,
            { newRate }
        );
    }

    getChildOrders(orderId: string) {
        return this.http.get<ApiResponse<OrderDto[]>>(
            `${this.base}/orders/${orderId}/children`
        );
    }

    getOrderLineage(orderId: string) {
        return this.http.get<ApiResponse<OrderLineageDto[]>>(
            `${this.base}/orders/${orderId}/lineage`
        );
    }

    adminGetOrderLineage(orderId: string) {
        return this.http.get<ApiResponse<OrderLineageDto[]>>(
            `${this.base}/orders/admin/${orderId}/lineage`
        );
    }

    getCountries(page: number) {
        return this.http.get<ApiResponse<CountryDto[]>>(
            `${this.base}/country?page=${page}&size=${this.pageSize}`
        );
    }

    getCurrencyCountry(countryCode: string, page: number, size = this.pageSize) {
        return this.http.get<ApiResponse<CountryCurrencyDto[]>>(
            `${this.base}/currency/country/${countryCode}?page=${page}&size=${size}`
        );
    }

    addCurrencyCountry(payload: AddCurrencyCountry) {
        return this.http.post<ApiResponse<CountryCurrencyDto>>(
            `${this.base}/currency/country`,
            payload
        );
    }

    updateCountry(payload: Partial<CountryDto>) {
        return this.http.put<ApiResponse<CountryDto>>(
            `${this.base}/country`,
            payload
        );
    }

    getCurrencies(page: number, size = this.pageSize) {
        return this.http.get<ApiResponse<CurrencyDetailsDto[]>>(
            `${this.base}/currency?page=${page}&size=${size}`
        );
    }

    updateCurrency(payload: Partial<CurrencyDetailsDto>) {
        return this.http.put<ApiResponse<CurrencyDetailsDto>>(
            `${this.base}/currency`,
            payload
        );
    }

    deleteCountry(id: string) {
        return this.http.delete<ApiResponse<string>>(`${environment.apiBaseUrl}/country/${id}`);
    }

    deleteCountryCurrency(id: string) {
        return this.http.delete<ApiResponse<string>>(`${environment.apiBaseUrl}/currency/country/${id}`);
    }

    deleteCurrency(id: string) {
        return this.http.delete<ApiResponse<string>>(`${environment.apiBaseUrl}/currency/${id}`);
    }

    deleteUser(id: string) {
        return this.http.delete<ApiResponse<string>>(`${environment.apiBaseUrl}/users/${id}`);
    }

    verifyFlutterwaveRedirect(payload: VerifyFlutterwaveRedirectRequest) {
        return this.http.post<ApiResponse<DepositStatusResponse>>(
            `${this.base}/payments/flutterwave/verify`,
            payload
        );
    }

    getDepositStatus(externalReference: string) {
        return this.http.get<ApiResponse<DepositStatusResponse>>(
            `${this.base}/payments/deposits/status/${encodeURIComponent(externalReference)}`
        );
    }

    getPaymentGatewayProviders() {
        return this.http.get<ApiResponse<PaymentGatewayProviderDto[]>>(
            `${this.base}/admin/payment-gateways/providers`
        );
    }

    createPaymentGatewayProvider(payload: CreatePaymentGatewayProviderRequest) {
        return this.http.post<ApiResponse<PaymentGatewayProviderDto>>(
            `${this.base}/admin/payment-gateways/providers`,
            payload
        );
    }

    getPaymentGatewayConfigs() {
        return this.http.get<ApiResponse<PaymentGatewayConfigDto[]>>(
            `${this.base}/admin/payment-gateways/configs`
        );
    }

    createPaymentGatewayConfig(payload: CreatePaymentGatewayConfigRequest) {
        return this.http.post<ApiResponse<PaymentGatewayConfigDto>>(
            `${this.base}/admin/payment-gateways/configs`,
            payload
        );
    }

    getCurrencyPaymentGateways(currencyCode?: string) {
        const url = currencyCode
            ? `${this.base}/admin/payment-gateways/currency-mappings?currencyCode=${encodeURIComponent(currencyCode)}`
            : `${this.base}/admin/payment-gateways/currency-mappings`;

        return this.http.get<ApiResponse<CurrencyPaymentGatewayDto[]>>(url);
    }

    createCurrencyPaymentGateway(payload: CreateCurrencyPaymentGatewayRequest) {
        return this.http.post<ApiResponse<CurrencyPaymentGatewayDto>>(
            `${this.base}/admin/payment-gateways/currency-mappings`,
            payload
        );
    }

    updateCurrencyPaymentGateway(id: string, payload: CreateCurrencyPaymentGatewayRequest) {
        return this.http.put<ApiResponse<CurrencyPaymentGatewayDto>>(
            `${this.base}/admin/payment-gateways/currency-mappings/${id}`,
            payload
        );
    }

    setCurrencyPaymentGatewayDefault(id: string) {
        return this.http.put<ApiResponse<CurrencyPaymentGatewayDto>>(
            `${this.base}/admin/payment-gateways/currency-mappings/${id}/default`,
            {}
        );
    }

    updatePaymentGatewayProvider(id: string, payload: any) {
        return this.http.put<ApiResponse<PaymentGatewayProviderDto>>(
            `${this.base}/admin/payment-gateways/providers/${id}`,
            payload
        );
    }

    togglePaymentGatewayProviderStatus(id: string) {
        return this.http.put<ApiResponse<PaymentGatewayProviderDto>>(
            `${this.base}/admin/payment-gateways/providers/${id}/toggle-status`,
            {}
        );
    }

    updatePaymentGatewayConfig(id: string, payload: any) {
        return this.http.put<ApiResponse<PaymentGatewayConfigDto>>(
            `${this.base}/admin/payment-gateways/configs/${id}`,
            payload
        );
    }

    togglePaymentGatewayConfigStatus(id: string) {
        return this.http.put<ApiResponse<PaymentGatewayConfigDto>>(
            `${this.base}/admin/payment-gateways/configs/${id}/toggle-status`,
            {}
        );
    }

    toggleCurrencyPaymentGatewayStatus(id: string) {
        return this.http.put<ApiResponse<CurrencyPaymentGatewayDto>>(
            `${this.base}/admin/payment-gateways/currency-mappings/${id}/toggle-status`,
            {}
        );
    }

    // ===============================
    // COUNTRY CHANGE
    // ===============================
    submitCountryChangeRequest(payload: CreateCountryChangeRequestDto) {
        return this.http.post<ApiResponse<UserCountryChangeRequestResponseDto>>(
            `${this.base}/Users/country-change-requests`,
            payload
        );
    }

    adminChangeCountry(payload: AdminCountryChangeRequestDto) {
        return this.http.post<ApiResponse<AdminCountryChangeRequestResponseDto>>(
            `${this.base}/Users/admin/country-change`,
            payload
        );
    }

    getCountryChangeRequests(filters: CountryChangeRequestFilters) {
        let params = new HttpParams();

        Object.entries(filters || {}).forEach(([key, value]) => {
            if (
                value !== undefined &&
                value !== null &&
                value !== '' &&
                value !== 'undefined'
            ) {
                params = params.set(key, String(value));
            }
        });

        return this.http.get<ApiResponse<CountryChangeRequestListItemDto[]>>(
            `${this.base}/Users/admin/country-change-requests`,
            { params }
        );
    }

    getCountryChangeRequest(requestId: string) {
        return this.http.get<ApiResponse<CountryChangeRequestDto>>(
            `${this.base}/Users/admin/country-change-requests/${requestId}`
        );
    }

    approveCountryChange(requestId: string, payload: ReviewCountryChangeRequestDto) {
        return this.http.post<ApiResponse<AdminCountryChangeRequestResponseDto>>(
            `${this.base}/Users/admin/country-change-requests/${requestId}/approve`,
            payload
        );
    }

    rejectCountryChange(requestId: string, payload: ReviewCountryChangeRequestDto) {
        return this.http.post<ApiResponse<AdminCountryChangeRequestResponseDto>>(
            `${this.base}/Users/admin/country-change-requests/${requestId}/reject`,
            payload
        );
    }

    cancelCountryChangeRequest(requestId: string) {
        return this.http.post<ApiResponse<CancelCountryChangeRequestResponseDto>>(`${this.base}/Users/country-change-requests/${requestId}/cancel`, {});
    }

    getDepositReceipt(depositId: string) {
        return this.http.get(`${this.base}/deposits/${depositId}/receipt`, {
            responseType: 'blob',
            observe: 'response'
        });
    }

    getWithdrawalReceipt(withdrawalId: string) {
        return this.http.get(`${this.base}/withdrawals/${withdrawalId}/receipt`, {
            responseType: 'blob',
            observe: 'response'
        });
    }

    getOpenComplianceFlags() {
        return this.http.get<ApiResponse<AdminComplianceFlagListItemDto[]>>(
            `${this.base}/Users/admin/compliance-flags`
        );
    }

    resolveComplianceFlag(flagId: string, payload: ResolveComplianceFlagRequestDto) {
        return this.http.post<ApiResponse<AdminComplianceFlagListItemDto>>(
            `${this.base}/Users/admin/compliance-flags/${flagId}/resolve`,
            payload
        );
    }

    saveInteracEmail(payload: UpsertInteracEmailRequestDto) {
        return this.http.post<ApiResponse<InteracEmailResponseDto>>(
            `${this.base}/deposits/interac-email`,
            payload
        );
    }

    getInteracEmail() {
        return this.http.get<ApiResponse<InteracEmailResponseDto | null>>(
            `${this.base}/deposits/interac-email`
        );
    }

    getCadDepositInstructions() {
        return this.http.get<ApiResponse<CadDepositInstructionsResponseDto>>(
            `${this.base}/deposits/cad/instructions`
        );
    }

    getVirtualAccount(currencyCode: string) {
        return this.http.get<ApiResponse<VirtualAccountResponseDto | null>>(
            `${this.base}/deposits/virtual-account/${encodeURIComponent(currencyCode)}`
        );
    }

    createVirtualAccount(currencyCode: string) {
        return this.http.post<ApiResponse<VirtualAccountResponseDto>>(
            `${this.base}/deposits/virtual-account/${encodeURIComponent(currencyCode)}`,
            {}
        );
    }

    getWalletVirtualAccounts(walletId: string) {
        return this.http.get<ApiResponse<VirtualAccountResponseDto[]>>(
            `${this.base}/deposits/wallets/${encodeURIComponent(walletId)}/virtual-accounts`
        );
    }

    createVirtualAccountByChannel(depositChannelId: string) {
        return this.http.post<ApiResponse<VirtualAccountResponseDto>>(
            `${this.base}/deposits/virtual-accounts/channels/${encodeURIComponent(depositChannelId)}`,
            {}
        );
    }

    refreshVirtualAccountById(virtualAccountId: string) {
        return this.http.post<ApiResponse<VirtualAccountResponseDto>>(
            `${this.base}/deposits/virtual-accounts/${encodeURIComponent(virtualAccountId)}/refresh`,
            {}
        );
    }

    getPaymentWebhookLogs(params: {
        providerCode?: string;
        processingStatus?: string;
        search?: string;
        fromDate?: string;
        toDate?: string;
        page?: number;
        rowsPerPage?: number;
    }) {
        return this.http.get<ApiResponse<PaymentWebhookLogListItemDto[]>>(
            `${this.base}/admin/payment-webhooks`,
            { params: this.toHttpParams(params) }
        );
    }
    refreshVirtualAccount(currencyCode: string) {
        return this.http.post<ApiResponse<VirtualAccountResponseDto>>(
            `${this.base}/Deposits/virtual-account/${currencyCode}/refresh`,
            {}
        );
    }

    resolveBlaaizCadDepositWebhook(id: string, userId: string, note?: string) {
        return this.http.post<ApiResponse<DepositDto>>(
            `${this.base}/admin/payment-webhooks/${id}/resolve-blaaiz-cad-deposit`,
            { userId, note }
        );
    }

    getPaymentWebhookLog(id: string) {
        return this.http.get<ApiResponse<PaymentWebhookLogDetailsDto>>(
            `${this.base}/admin/payment-webhooks/${id}`
        );
    }

    getAdminMenuCounters() {
        return this.http.get<ApiResponse<AdminMenuCountersDto>>(
            `${this.base}/admin/menu-counters`
        );
    }

    // ===============================
    // ADMIN FINANCE REPORTING
    // ===============================
    getAdminFinancePnlReport(filters: FinanceReportFilters) {
        return this.http.get<ApiResponse<AdminFinancePnlReportDto>>(
            `${this.base}/admin/finance/reports/pnl`,
            { params: this.toHttpParams(filters) }
        );
    }

    getAdminFinanceTradeSnapshots(filters: FinanceReportFilters) {
        return this.http.get<ApiResponse<TradeProfitSnapshotListItemDto[]>>(
            `${this.base}/admin/finance/reports/trades`,
            { params: this.toHttpParams(filters) }
        );
    }

    exportAdminFinanceTradeSnapshots(filters: FinanceReportFilters) {
        return this.http.get(`${this.base}/admin/finance/reports/trades/export`, {
            params: this.toHttpParams(filters),
            responseType: 'blob',
            observe: 'response'
        });
    }

    getFinanceReferenceRates(filters: {
        currencyCode?: string;
        fromDate?: string;
        toDate?: string;
    }) {
        return this.http.get<ApiResponse<FinanceReferenceRateDto[]>>(
            `${this.base}/admin/finance/reference-rates`,
            { params: this.toHttpParams(filters) }
        );
    }

    saveFinanceReferenceRate(payload: UpsertFinanceReferenceRateDto) {
        return this.http.post<ApiResponse<FinanceReferenceRateDto>>(
            `${this.base}/admin/finance/reference-rates`,
            payload
        );
    }

    toHttpParams(obj: any): HttpParams {
        let params = new HttpParams();

        Object.keys(obj || {}).forEach((key) => {
            const value = obj[key];

            if (
                value !== undefined &&
                value !== null &&
                value !== ''
            ) {
                params = params.set(key, value);
            }
        });

        return params;
    }
}
